/**
 * Devices & provisioning (brief §4/§4H, ADR 0026).
 *
 * The adoption queue (approve releases full config; reject is permanent),
 * open-provisioning window controls (scoped, capped, audited server-side),
 * the fleet inventory with rendered-config preview, and firmware
 * images/rollouts with activate/rollback.
 */

import { useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  activateRollout,
  approveAdoption,
  closeProvisioningWindow,
  createFirmwareRollout,
  deviceConfigUrl,
  getActiveWindows,
  getAdoptions,
  getDevices,
  getFirmwareImages,
  getFirmwareRollouts,
  openProvisioningWindow,
  rejectAdoption,
  resyncDevice,
  confirmDeviceRebooted,
  getDeviceLines,
  assignDeviceLine,
  removeDeviceLine,
  updateDeviceLine,
  rotateDeviceSecret,
  rollbackRollout,
  uploadFirmwareImage,
  getDiscoveredDevices,
  getE911Options,
  preRegisterDiscovered,
  setDiscoveredState,
  devicesExportUrl,
  importDevicesCsv,
} from "@pbxmgr/api";
import { ApiError } from "@pbxmgr/api";
import type {
  DeviceImportReport,
  DeviceRow,
  DeviceLinesInfo,
  DiscoveredDeviceRow,
  ResyncResult,
} from "@pbxmgr/api";
import { Alert, Badge, Button, ConfirmButton, Spinner, TextField } from "@pbxmgr/ui";
import type { BadgeTone } from "@pbxmgr/ui";

import { usePermission } from "../auth/usePermission";
import { Panel } from "../dashboard/panels";
import {
  UPLOAD_LIMIT_MB,
  exceedsLimit,
  formatMB,
  guessFromFilename,
  uploadErrorMessage,
} from "./firmwareUpload";
import { RENDER_CAP, describeTarget, filterTargets } from "./rolloutTargets";

const ADOPTION_TONE: Record<string, BadgeTone> = {
  adopted: "online",
  pending: "degraded",
  pre_registered: "unknown",
  rejected: "offline",
};
const ROLLOUT_TONE: Record<string, BadgeTone> = {
  // The API creates rollouts as "draft" (activate from draft or paused);
  // the Activate button was keyed on a "staged" state that never existed,
  // so no rollout could be taken live from the UI (found 2026-09-13).
  draft: "unknown",
  paused: "degraded",
  active: "online",
  staged: "unknown",
  rolled_back: "offline",
  complete: "online",
};

function formatWhen(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

function AdoptionQueue(): ReactNode {
  const canDecide = usePermission("provisioning.push");
  const qc = useQueryClient();
  const adoptions = useQuery({
    queryKey: ["prov", "adoptions"],
    queryFn: getAdoptions,
  });
  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: ["prov"] });
  };
  const approve = useMutation({
    mutationFn: approveAdoption,
    onSuccess: invalidate,
  });
  const reject = useMutation({
    mutationFn: rejectAdoption,
    onSuccess: invalidate,
  });
  const pending = adoptions.data?.pending ?? [];

  return (
    <Panel title={`Adoption queue (${pending.length})`}>
      {pending.length === 0 ? (
        <div className="ui-empty">No devices awaiting a decision.</div>
      ) : (
        <table className="ui-table">
          <thead>
            <tr>
              <th>MAC</th>
              <th>Model</th>
              <th>State</th>
              <th>Seen from</th>
              <th>Cert pinned</th>
              <th>Decision</th>
            </tr>
          </thead>
          <tbody>
            {pending.map((d) => (
              <tr key={d.device_id}>
                <td>{d.mac}</td>
                <td>
                  {d.vendor} {d.model}
                </td>
                <td>
                  <Badge tone={ADOPTION_TONE[d.adoption_state] ?? "unknown"}>
                    {d.adoption_state}
                  </Badge>
                </td>
                <td>{d.last_seen_ip ?? "—"}</td>
                <td>{d.pinned_cert_fingerprint ? "yes" : "—"}</td>
                <td>
                  {canDecide ? (
                    <div className="pairing-actions">
                      <Button
                        disabled={approve.isPending}
                        onClick={() => approve.mutate(d.device_id)}
                      >
                        Approve
                      </Button>
                      <Button
                        variant="secondary"
                        disabled={reject.isPending}
                        onClick={() => reject.mutate(d.device_id)}
                      >
                        Reject
                      </Button>
                    </div>
                  ) : (
                    <span className="ui-empty">read-only</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Panel>
  );
}

function WindowControls(): ReactNode {
  const canOpen = usePermission("provisioning.window.open");
  const qc = useQueryClient();
  const windows = useQuery({
    queryKey: ["prov", "windows"],
    queryFn: getActiveWindows,
  });
  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: ["prov", "windows"] });
  };

  const [minutes, setMinutes] = useState("15");
  const [scopeKind, setScopeKind] = useState("source_cidr");
  const [scopeValue, setScopeValue] = useState("");
  const [reason, setReason] = useState("");
  const [internetAllowed, setInternetAllowed] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const open = useMutation({
    mutationFn: () =>
      openProvisioningWindow({
        minutes: Number(minutes),
        scope_kind: scopeKind,
        scope_value: scopeValue
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
        // §4H: a public-source phone is served only by an internet-reachable
        // window (MAC-list scope) and only with a verified factory cert.
        internet_allowed: scopeKind === "mac_list" ? internetAllowed : false,
        reason: reason || undefined,
      }),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (err) =>
      setError(
        err instanceof ApiError ? (err.detail ?? err.message) : "failed",
      ),
  });
  const close = useMutation({
    mutationFn: closeProvisioningWindow,
    onSuccess: invalidate,
  });

  const active = windows.data?.items ?? [];

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    open.mutate();
  };

  return (
    <Panel title={`Open-provisioning windows (${active.length} active)`}>
      {active.length > 0 ? (
        <table className="ui-table">
          <thead>
            <tr>
              <th>Scope</th>
              <th>Expires</th>
              <th>Requests</th>
              <th>Reason</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {active.map((w) => (
              <tr key={w.id}>
                <td>
                  {w.scope_kind}: {(w.scope_value ?? []).join(", ")}
                  {w.internet_allowed ? " (internet)" : ""}
                </td>
                <td>{formatWhen(w.expires_at)}</td>
                <td>{w.request_count}</td>
                <td>{w.reason ?? "—"}</td>
                <td>
                  {canOpen ? (
                    <Button
                      variant="secondary"
                      disabled={close.isPending}
                      onClick={() => close.mutate(w.id)}
                    >
                      Close now
                    </Button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="ui-empty">
          Closed (the default). A window admits in-scope devices to{" "}
          <em>bootstrap only</em> — full configuration still requires adoption.
        </div>
      )}

      {canOpen ? (
        <form className="window-form" onSubmit={submit}>
          <TextField
            id="win-minutes"
            label="Minutes (max 60)"
            value={minutes}
            onChange={(e) => setMinutes(e.target.value)}
          />
          <div className="ui-field">
            <label htmlFor="win-scope-kind">Scope</label>
            <select
              id="win-scope-kind"
              value={scopeKind}
              onChange={(e) => setScopeKind(e.target.value)}
            >
              <option value="source_cidr">source CIDR</option>
              <option value="mac_list">MAC list</option>
              <option value="vendor_oui">vendor OUI</option>
            </select>
          </div>
          <TextField
            id="win-scope-value"
            label="Scope entries (comma-separated)"
            value={scopeValue}
            onChange={(e) => setScopeValue(e.target.value)}
            placeholder="192.168.200.0/24"
          />
          <TextField
            id="win-reason"
            label="Reason (audited)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          {scopeKind === "mac_list" ? (
            <div className="ui-field">
              <label htmlFor="win-internet">
                <input
                  id="win-internet"
                  type="checkbox"
                  checked={internetAllowed}
                  onChange={(e) => setInternetAllowed(e.target.checked)}
                />{" "}
                Internet-reachable — admit these MACs from public addresses (a
                verified Snom factory certificate is still required, §4H)
              </label>
            </div>
          ) : (
            <p className="ui-hint">
              Phones arriving from a public address need a MAC-list window
              marked internet-reachable.
            </p>
          )}
          <Button type="submit" disabled={open.isPending || !scopeValue}>
            Open window
          </Button>
        </form>
      ) : null}
      {error ? <Alert>{error}</Alert> : null}
    </Panel>
  );
}

/** One line per attempted path, e.g. "node-resync: uncaptured". */
function resyncSummary(r: ResyncResult): string {
  const paths = r.paths.map((p) => `${p.path}: ${p.outcome}`).join(" · ");
  const head = r.triggered
    ? "triggered"
    : r.deferred
      ? "deferred"
      : "no path fired (backstop will retry)";
  return `${r.mac} (${r.classification}) — ${head} · ${paths}`;
}

/**
 * Phones the importer found provisioned on the nodes (ADR 0048): the
 * onboarding path for a fleet Vodia already provisions. Confirming the
 * proposed location pre-registers the phone (zero-touch on its first fetch
 * with its factory certificate) and attests it there (ADR 0047). Lines are
 * bound later, at the swap — never here (ADR 0038 would rotate the SIP
 * password under the phone Vodia still provisions).
 */
/**
 * Bulk pre-registration from a spreadsheet (ADR 0050): the path for phones
 * no node knows yet (new employees). Dry-run first — every problem is named
 * per line — then import: each phone is pre-registered, placed (and
 * attested, ADR 0047, when the importer holds e911.manage) and its lines
 * bound, so it provisions and registers on its first fetch.
 */
function DeviceImport(): ReactNode {
  const canPush = usePermission("provisioning.push");
  const qc = useQueryClient();
  const [csv, setCsv] = useState("");
  const [report, setReport] = useState<DeviceImportReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const onError = (err: unknown): void =>
    setError(
      err instanceof ApiError ? (err.detail ?? err.message) : "import failed",
    );
  const dryRun = useMutation({
    mutationFn: () => importDevicesCsv(csv, true),
    onSuccess: (r) => {
      setError(null);
      setReport(r);
    },
    onError,
  });
  const commit = useMutation({
    mutationFn: () => importDevicesCsv(csv, false),
    onSuccess: (r) => {
      setError(null);
      setReport(r);
      void qc.invalidateQueries({ queryKey: ["prov"] });
      void qc.invalidateQueries({ queryKey: ["e911"] });
    },
    onError,
  });
  if (!canPush) return null;
  return (
    <Panel title="Pre-register from a spreadsheet">
      <p className="ui-hint">
        Columns: mac, model (default D815), serial, location, label,
        primary_extension, primary_node, primary_domain, backup_extension,
        backup_node, backup_domain, button_template, sidecar (0/1). Only mac is
        required. Extensions must already exist on the node (the inventory); the
        import binds them, so the phone registers on its first fetch. Validate
        first; the import skips rows with problems and names them.
      </p>
      <textarea
        aria-label="device CSV"
        rows={6}
        value={csv}
        onChange={(e) => setCsv(e.target.value)}
        placeholder="mac,location,label,primary_extension,primary_node,backup_extension,backup_node,button_template,sidecar"
      />
      <div className="dash__status">
        <Button
          variant="secondary"
          disabled={!csv || dryRun.isPending}
          onClick={() => dryRun.mutate()}
        >
          Validate (dry run)
        </Button>
        <Button
          disabled={!csv || commit.isPending || report?.valid === false}
          onClick={() => commit.mutate()}
        >
          Import
        </Button>
        <a href={devicesExportUrl()}>Export devices CSV</a>
      </div>
      {error ? <Alert>{error}</Alert> : null}
      {report ? (
        <div>
          <p>
            {report.dry_run
              ? `${report.parsed_rows} row(s): ${report.would_create} would be created, ${report.would_place} placed, ${report.would_bind_lines} line(s) bound`
              : `${report.created} created · ${report.lines_bound} line(s) bound · ${report.attested} attested · ${report.skipped} skipped`}
          </p>
          {report.errors.length > 0 ? (
            <Alert>
              <ul>
                {report.errors.map((e) => (
                  <li key={`${e.line}-${e.field}-${e.message}`}>
                    line {e.line} {e.field}: {e.message}
                  </li>
                ))}
              </ul>
            </Alert>
          ) : null}
        </div>
      ) : null}
    </Panel>
  );
}

/** Short labels for the ADR 0053 location signals, in the order the API returns them. */
const SIGNAL_LABELS: Record<string, string> = {
  vodia_assignment: "Vodia assignment",
  carrier_eani: "EANI → carrier",
  lan_ip: "LAN",
  public_ip: "Public",
};

function DiscoveredDevices(): ReactNode {
  const canPush = usePermission("provisioning.push");
  const canAttest = usePermission("e911.manage");
  const qc = useQueryClient();
  const discovered = useQuery({
    queryKey: ["prov", "discovered"],
    queryFn: getDiscoveredDevices,
  });
  const options = useQuery({
    queryKey: ["e911", "options"],
    queryFn: getE911Options,
  });
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [overrides, setOverrides] = useState<Record<number, string>>({});
  const [showAll, setShowAll] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: ["prov"] });
    void qc.invalidateQueries({ queryKey: ["e911"] });
  };
  const onError = (err: unknown): void =>
    setError(
      err instanceof ApiError ? (err.detail ?? err.message) : "request failed",
    );

  const preRegister = useMutation({
    mutationFn: () =>
      preRegisterDiscovered(
        [...selected].map((id) => {
          const chosen = overrides[id];
          return chosen === undefined
            ? { id }
            : { id, location_id: chosen ? Number(chosen) : null };
        }),
        canAttest,
      ),
    onSuccess: (out) => {
      setError(null);
      const created = out.items.filter((i) => i.created).length;
      const attested = out.items.filter((i) => i.attested).length;
      const unknown = out.items
        .filter((i) => !i.profile_known)
        .map((i) => i.mac);
      setResult(
        `${out.count} phone(s): ${created} pre-registered, ${attested} attested` +
          (unknown.length > 0
            ? ` — no provisioning profile for ${unknown.join(", ")}`
            : ""),
      );
      setSelected(new Set());
      invalidate();
    },
    onError,
  });
  const ignore = useMutation({
    mutationFn: (state: "ignored" | "new") =>
      setDiscoveredState([...selected], state),
    onSuccess: () => {
      setError(null);
      setSelected(new Set());
      invalidate();
    },
    onError,
  });

  const all = discovered.data?.items ?? [];
  const rows = showAll ? all : all.filter((r) => r.state === "new");
  const counts = discovered.data?.counts ?? {};
  const toggle = (id: number): void =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const selectableIds = rows
    .filter((r) => r.state !== "pre_registered")
    .map((r) => r.id);

  return (
    <Panel
      title={`Discovered on the PBX (${counts.new ?? 0} new, ${counts.pre_registered ?? 0} on the platform, ${counts.ignored ?? 0} ignored)`}
    >
      <p className="ui-hint">
        Every phone the nodes provision today, refreshed with the inventory. The
        location is proposed from where the phone registers from (its IP against
        Buildings &amp; IPs). Confirming it pre-registers the phone so its
        replacement, or its own first fetch with a factory certificate, needs no
        window — and attests it there (ADR 0047). Extensions are bound at the
        swap, not here.
      </p>
      <div className="dash__status">
        <Button onClick={() => setShowAll((v) => !v)}>
          {showAll ? "Show new only" : "Show all"}
        </Button>
        <Button
          onClick={() =>
            setSelected(
              selected.size === selectableIds.length
                ? new Set()
                : new Set(selectableIds),
            )
          }
        >
          {selected.size === selectableIds.length && selectableIds.length > 0
            ? "Clear selection"
            : "Select all shown"}
        </Button>
        {canPush ? (
          <>
            <Button
              disabled={selected.size === 0 || preRegister.isPending}
              onClick={() => preRegister.mutate()}
            >
              Pre-register selected{canAttest ? " + attest location" : ""} (
              {selected.size})
            </Button>
            <Button
              disabled={selected.size === 0 || ignore.isPending}
              onClick={() => ignore.mutate("ignored")}
            >
              Ignore
            </Button>
            {showAll ? (
              <Button
                disabled={selected.size === 0 || ignore.isPending}
                onClick={() => ignore.mutate("new")}
              >
                Un-ignore
              </Button>
            ) : null}
          </>
        ) : null}
      </div>
      {result ? <Alert>{result}</Alert> : null}
      {error ? <Alert>{error}</Alert> : null}
      {discovered.isLoading ? (
        <Spinner />
      ) : rows.length === 0 ? (
        <div className="ui-empty">
          {all.length === 0
            ? "Nothing discovered yet — the inventory import runs hourly (Pairs → Refresh inventory runs it now)."
            : "No new phones."}
        </div>
      ) : (
        <table className="ui-table">
          <thead>
            <tr>
              <th></th>
              <th>MAC</th>
              <th>Model</th>
              <th>Firmware</th>
              <th>Node / domain</th>
              <th>Extensions</th>
              <th>Registers from</th>
              <th>Location</th>
              <th>State</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r: DiscoveredDeviceRow) => (
              <tr key={r.id}>
                <td>
                  {r.state === "pre_registered" ? null : (
                    <input
                      type="checkbox"
                      aria-label={`select ${r.mac}`}
                      checked={selected.has(r.id)}
                      onChange={() => toggle(r.id)}
                    />
                  )}
                </td>
                <td>
                  {r.mac}
                  {r.name ? <div className="ui-hint">{r.name}</div> : null}
                </td>
                <td>
                  {r.vendor} {r.model}
                  {r.profile_known ? null : (
                    <div>
                      <Badge tone="degraded">no profile</Badge>
                    </div>
                  )}
                </td>
                <td>{r.firmware_version ?? "—"}</td>
                <td>
                  {r.node_name ?? r.node_id} / {r.domain}
                </td>
                <td>
                  {r.extensions.join(", ") || "—"}
                  {r.has_buttons ? (
                    <div className="ui-hint">buttons on the PBX</div>
                  ) : null}
                </td>
                <td>
                  {r.registering_ip ?? "—"}
                  {r.local_ip ? <div className="ui-hint">LAN {r.local_ip}</div> : null}
                </td>
                <td>
                  {r.location_conflict ? (
                    <div>
                      <Badge tone="offline">conflict</Badge>
                    </div>
                  ) : null}
                  {r.state === "pre_registered" ? (
                    (r.proposed_location_name ?? "—")
                  ) : (
                    <select
                      aria-label={`location for ${r.mac}`}
                      value={
                        overrides[r.id] ??
                        (r.proposed_location_id === null
                          ? ""
                          : String(r.proposed_location_id))
                      }
                      onChange={(e) =>
                        setOverrides({ ...overrides, [r.id]: e.target.value })
                      }
                    >
                      <option value="">— none —</option>
                      {(options.data?.locations ?? []).map((loc) => (
                        <option key={loc.id} value={loc.id}>
                          {loc.name}
                          {loc.id === r.proposed_location_id
                            ? " (proposed)"
                            : ""}
                        </option>
                      ))}
                    </select>
                  )}
                  {r.location_signals.length ? (
                    // ADR 0053: every signal is shown, resolved or not, so a
                    // disagreement is visible instead of silently decided.
                    <ul className="ui-hint signal-list">
                      {r.location_signals.map((sig) => (
                        <li key={sig.signal}>
                          {SIGNAL_LABELS[sig.signal]}: {sig.value ?? "—"}
                          {sig.location_name ? ` → ${sig.location_name}` : ""}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </td>
                <td>
                  <Badge
                    tone={
                      r.state === "pre_registered"
                        ? "online"
                        : r.state === "ignored"
                          ? "unknown"
                          : "degraded"
                    }
                  >
                    {r.state === "pre_registered" ? "on the platform" : r.state}
                  </Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Panel>
  );
}

function DeviceInventory(): ReactNode {
  const canPush = usePermission("provisioning.push");
  const qc = useQueryClient();
  const devices = useQuery({
    queryKey: ["prov", "devices"],
    queryFn: getDevices,
  });
  const [lastResync, setLastResync] = useState<ResyncResult | null>(null);
  const [linesFor, setLinesFor] = useState<DeviceRow | null>(null);
  const [error, setError] = useState<string | null>(null);

  const resync = useMutation({
    mutationFn: (deviceId: number) => resyncDevice(deviceId),
    onSuccess: (result) => {
      setError(null);
      setLastResync(result);
      void qc.invalidateQueries({ queryKey: ["prov", "devices"] });
    },
    onError: (err) =>
      setError(
        err instanceof ApiError ? (err.detail ?? err.message) : "resync failed",
      ),
  });

  const rebooted = useMutation({
    mutationFn: (deviceId: number) => confirmDeviceRebooted(deviceId),
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ["prov", "devices"] });
    },
    onError: (err) =>
      setError(
        err instanceof ApiError
          ? (err.detail ?? err.message)
          : "could not clear the reboot flag",
      ),
  });

  const rows = devices.data?.items ?? [];
  return (
    <Panel title={`Devices (${devices.data?.total ?? 0})`}>
      {devices.isLoading ? (
        <Spinner />
      ) : rows.length === 0 ? (
        <div className="ui-empty">
          No devices yet — enroll via a provisioning window.
        </div>
      ) : (
        <table className="ui-table">
          <thead>
            <tr>
              <th>MAC</th>
              <th>Model</th>
              <th>Label</th>
              <th>Adoption</th>
              <th>Provisioning</th>
              <th>Firmware</th>
              <th>Last seen</th>
              <th>Config</th>
              {canPush ? <th></th> : null}
            </tr>
          </thead>
          <tbody>
            {rows.map((d) => (
              <tr key={d.id}>
                <td>{d.mac}</td>
                <td>
                  {d.vendor} {d.model}
                </td>
                <td>{d.label ?? "—"}</td>
                <td>
                  <Badge tone={ADOPTION_TONE[d.adoption_state] ?? "unknown"}>
                    {d.adoption_state}
                  </Badge>
                </td>
                <td>
                  {d.provisioning_state === "current" ? (
                    d.provisioning_state
                  ) : (
                    <Badge
                      tone={
                        d.provisioning_state === "error"
                          ? "offline"
                          : "degraded"
                      }
                    >
                      {d.provisioning_state}
                    </Badge>
                  )}
                  {d.reboot_needed_since ? (
                    <>
                      {" "}
                      <span
                        title={`${d.reboot_reason ?? "subscription change"} — since ${d.reboot_needed_since}`}
                      >
                        <Badge tone="degraded">reboot needed</Badge>
                      </span>
                    </>
                  ) : null}
                </td>
                <td>{d.firmware_current ?? "—"}</td>
                <td>{d.last_seen_ip ?? "—"}</td>
                <td>
                  {d.adoption_state === "adopted" ? (
                    <a
                      href={deviceConfigUrl(d.mac)}
                      target="_blank"
                      rel="noreferrer"
                    >
                      preview
                    </a>
                  ) : (
                    "—"
                  )}
                </td>
                {canPush ? (
                  <td>
                    {d.adoption_state === "adopted" ? (
                      <div className="pairing-actions">
                        <Button
                          variant="secondary"
                          disabled={resync.isPending}
                          onClick={() => resync.mutate(d.id)}
                        >
                          Resync now
                        </Button>
                        <Button
                          variant="secondary"
                          onClick={() => setLinesFor(d)}
                        >
                          Lines…
                        </Button>
                        {d.reboot_needed_since ? (
                          <Button
                            variant="secondary"
                            disabled={rebooted.isPending}
                            title={d.reboot_reason ?? undefined}
                            onClick={() => rebooted.mutate(d.id)}
                          >
                            Rebooted
                          </Button>
                        ) : null}
                      </div>
                    ) : null}
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {lastResync ? <p>{resyncSummary(lastResync)}</p> : null}
      {error ? <Alert>{error}</Alert> : null}
      {linesFor ? (
        <DeviceLinesPanel
          key={linesFor.id}
          device={linesFor}
          onClose={() => setLinesFor(null)}
        />
      ) : null}
    </Panel>
  );
}

/**
 * Bind SIP identities to a device (ADR 0038): pick a node and one of its
 * inventory domains, name the extension, choose the slot/role and whether
 * this is the E911 line. Protected permission: typed-MAC confirmation.
 */
function DeviceLinesPanel({
  device,
  onClose,
}: {
  device: DeviceRow;
  onClose: () => void;
}): ReactNode {
  const canBind = usePermission("provisioning.templates.manage");
  const qc = useQueryClient();
  const info = useQuery({
    queryKey: ["prov", "lines", device.id],
    queryFn: () => getDeviceLines(device.id),
  });
  const [confirm, setConfirm] = useState("");
  const [nodeId, setNodeId] = useState("");
  const [domain, setDomain] = useState("");
  const [extension, setExtension] = useState("");
  const [role, setRole] = useState("primary");
  const [isE911, setIsE911] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const data: DeviceLinesInfo | undefined = info.data;
  const node = (data?.nodes ?? []).find((n) => String(n.id) === nodeId);
  const confirmed = confirm.trim().toLowerCase() === device.mac;

  const bind = useMutation({
    mutationFn: () =>
      assignDeviceLine(device.id, {
        extension: extension.trim(),
        node_id: Number(nodeId),
        domain,
        line_index: data?.next_line_index ?? 1,
        role,
        is_e911: isE911,
      }),
    onSuccess: (r) => {
      setError(null);
      setDone(
        `Line ${r.line_index}: ${r.extension} bound — the SIP password was set on the node.`,
      );
      setExtension("");
      setIsE911(false);
      void qc.invalidateQueries({ queryKey: ["prov"] });
      void qc.invalidateQueries({ queryKey: ["e911"] });
    },
    onError: (err) =>
      setError(
        err instanceof ApiError
          ? (err.detail ?? err.message)
          : "binding failed",
      ),
  });

  const edit = useMutation({
    mutationFn: (args: { lineId: number; role?: string; is_e911?: boolean }) =>
      updateDeviceLine(device.id, args.lineId, {
        role: args.role,
        is_e911: args.is_e911,
      }),
    onSuccess: (r) => {
      setError(null);
      setDone(`Line updated: ${r.role}${r.is_e911_line ? ", E911 line" : ""}.`);
      void qc.invalidateQueries({ queryKey: ["prov"] });
      void qc.invalidateQueries({ queryKey: ["e911"] });
    },
    onError: (err) =>
      setError(err instanceof ApiError ? (err.detail ?? err.message) : "update failed"),
  });
  const unbind = useMutation({
    mutationFn: (lineId: number) => removeDeviceLine(device.id, lineId),
    onSuccess: (r) => {
      setError(null);
      setDone(
        r.sip_auth_rotated
          ? "Line removed — the account's SIP password was rotated on the node."
          : `Line removed — the account keeps its password (${r.shared_with} other phone(s) hold it).`,
      );
      void qc.invalidateQueries({ queryKey: ["prov"] });
      void qc.invalidateQueries({ queryKey: ["e911"] });
    },
    onError: (err) =>
      setError(err instanceof ApiError ? (err.detail ?? err.message) : "removal failed"),
  });
  const rotate = useMutation({
    mutationFn: () => rotateDeviceSecret(device.id),
    onSuccess: (r) => {
      setError(null);
      setDone(`Provisioning secret rotated — ${r.note}.`);
      void qc.invalidateQueries({ queryKey: ["prov"] });
    },
    onError: (err) =>
      setError(
        err instanceof ApiError
          ? (err.detail ?? err.message)
          : "rotation failed",
      ),
  });

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    bind.mutate();
  };

  return (
    <Panel title={`Lines — ${device.mac} (${device.label ?? "unlabeled"})`}>
      {info.isLoading ? (
        <Spinner />
      ) : (data?.lines.length ?? 0) === 0 ? (
        <div className="ui-empty">
          No lines yet. The phone registers nothing until one is bound.
        </div>
      ) : (
        <table className="ui-table">
          <thead>
            <tr>
              <th>Slot</th>
              <th>Role</th>
              <th>Node</th>
              <th>Domain</th>
              <th>Extension</th>
              <th>E911 line</th>
              {canBind ? <th></th> : null}
            </tr>
          </thead>
          <tbody>
            {data?.lines.map((ln) => (
              <tr key={ln.id}>
                <td>{ln.line_index}</td>
                <td>
                  {canBind ? (
                    <select
                      aria-label={`role of line ${ln.line_index}`}
                      value={ln.role}
                      disabled={!confirmed || edit.isPending}
                      onChange={(e) => edit.mutate({ lineId: ln.id, role: e.target.value })}
                    >
                      <option value="primary">primary</option>
                      <option value="backup">backup</option>
                      <option value="monitor">monitor</option>
                    </select>
                  ) : (
                    ln.role
                  )}
                </td>
                <td>{ln.node_name}</td>
                <td>{ln.domain ?? "—"}</td>
                <td>{ln.extension}</td>
                <td>
                  {canBind ? (
                    <input
                      type="checkbox"
                      aria-label={`E911 line ${ln.line_index}`}
                      checked={ln.is_e911_line}
                      disabled={!confirmed || edit.isPending}
                      onChange={(e) => edit.mutate({ lineId: ln.id, is_e911: e.target.checked })}
                    />
                  ) : ln.is_e911_line ? (
                    "yes"
                  ) : (
                    "—"
                  )}
                </td>
                {canBind ? (
                  <td>
                    <ConfirmButton
                      label="Remove"
                      question={`Unbind line ${ln.line_index} (${ln.extension} on ${ln.node_name})? The phone drops the identity at its next fetch.`}
                      confirmLabel="Yes, remove line"
                      disabled={!confirmed || unbind.isPending}
                      onConfirm={() => unbind.mutate(ln.id)}
                    />
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {canBind ? (
        <form className="window-form" onSubmit={submit}>
          <p className="ui-hint">
            Binding rotates that account's SIP password on the node (the
            platform owns it, ADR 0038); the phone picks the identity up at its
            next config fetch or reboot. Slot {data?.next_line_index ?? 1} is
            next. Role and E911 flag change in place above; node, domain or
            extension are a remove and a new binding. The typed MAC unlocks all
            of it.
          </p>
          <TextField
            id={`lines-confirm-${device.id}`}
            label={`Type the device MAC (${device.mac}) to confirm — line binding is protected`}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
          <div className="ui-field">
            <label htmlFor={`lines-node-${device.id}`}>Node</label>
            <select
              id={`lines-node-${device.id}`}
              value={nodeId}
              onChange={(e) => {
                setNodeId(e.target.value);
                setDomain("");
              }}
            >
              <option value="">— choose —</option>
              {(data?.nodes ?? []).map((n) => (
                <option key={n.id} value={n.id}>
                  {n.name}
                </option>
              ))}
            </select>
          </div>
          <div className="ui-field">
            <label htmlFor={`lines-domain-${device.id}`}>
              Domain (from the node's inventory)
            </label>
            <select
              id={`lines-domain-${device.id}`}
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              disabled={!node}
            >
              <option value="">— choose —</option>
              {(node?.domains ?? []).map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </div>
          <TextField
            id={`lines-ext-${device.id}`}
            label="Extension (must exist on that node/domain)"
            value={extension}
            onChange={(e) => setExtension(e.target.value)}
          />
          <div className="ui-field">
            <label htmlFor={`lines-role-${device.id}`}>Role</label>
            <select
              id={`lines-role-${device.id}`}
              value={role}
              onChange={(e) => setRole(e.target.value)}
            >
              <option value="primary">
                primary (identity 1 on the pair's primary node)
              </option>
              <option value="backup">backup (the standby node)</option>
              <option value="monitor">monitor</option>
            </select>
          </div>
          <div className="ui-field">
            <label htmlFor={`lines-e911-${device.id}`}>
              <input
                id={`lines-e911-${device.id}`}
                type="checkbox"
                checked={isE911}
                onChange={(e) => setIsE911(e.target.checked)}
              />{" "}
              This is the E911 line (exactly one per device; the location's home
              node decides where 911 leaves, ADR 0041)
            </label>
          </div>
          <div className="pairing-actions">
            <Button
              type="submit"
              disabled={
                !confirmed ||
                !nodeId ||
                !domain ||
                !extension.trim() ||
                bind.isPending
              }
            >
              {bind.isPending ? "Binding…" : "Bind line"}
            </Button>
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
            <Button
              variant="secondary"
              disabled={!confirmed || rotate.isPending}
              onClick={() => rotate.mutate()}
            >
              Rotate provisioning secret
            </Button>
          </div>
          <p className="ui-hint">
            Rotate the secret when its URL has been seen somewhere it should not
            (a chat, a ticket). A phone with a pinned factory certificate picks
            up the new URL at its next fetch; one without must re-enrol through
            a window.
          </p>
        </form>
      ) : (
        <p className="ui-empty">
          Read-only — provisioning.templates.manage required to bind lines.
        </p>
      )}
      {done ? <p>{done}</p> : null}
      {error ? <Alert>{error}</Alert> : null}
    </Panel>
  );
}

function UploadImageForm({ onDone }: { onDone: () => void }): ReactNode {
  const [vendor, setVendor] = useState("snom");
  const [model, setModel] = useState("");
  const [version, setVersion] = useState("");
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [sha, setSha] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const upload = useMutation({
    mutationFn: () => {
      if (!file) throw new Error("no file selected");
      return uploadFirmwareImage({
        vendor,
        model,
        version,
        release_notes: notes || undefined,
        file,
      });
    },
    onSuccess: (image) => {
      setError(null);
      setSha(image.sha256);
      setModel("");
      setVersion("");
      setNotes("");
      setFile(null);
      onDone();
    },
    onError: (err) => setError(uploadErrorMessage(err)),
  });

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (file && exceedsLimit(file.size)) {
      setError(
        `${file.name} is ${formatMB(file.size)} — above the ${UPLOAD_LIMIT_MB} MB upload limit.`,
      );
      return;
    }
    upload.mutate();
  };

  return (
    <form className="window-form" onSubmit={submit}>
      <h3>Upload image</h3>
      <p className="ui-hint">
        Upload the vendor's image file here (no shell or FTP access needed);
        images up to {UPLOAD_LIMIT_MB} MB. The server computes the SHA-256 for
        you to check against the vendor's published checksum.
      </p>
      <TextField
        id="fw-vendor"
        label="Vendor"
        value={vendor}
        onChange={(e) => setVendor(e.target.value)}
      />
      <TextField
        id="fw-model"
        label="Model (required — filled from the filename)"
        value={model}
        onChange={(e) => setModel(e.target.value)}
        placeholder="e.g. D815"
      />
      <TextField
        id="fw-version"
        label="Version (required — filled from the filename)"
        value={version}
        onChange={(e) => setVersion(e.target.value)}
        placeholder="e.g. 10.1.226.13"
      />
      <TextField
        id="fw-notes"
        label="Release notes"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
      />
      <div className="ui-field">
        <label htmlFor="fw-file">Image file</label>
        <input
          id="fw-file"
          type="file"
          onChange={(e) => {
            const chosen = e.target.files?.[0] ?? null;
            setFile(chosen);
            // Snom names the image after the model and version; fill the
            // empty fields from it so the operator does not retype them.
            if (chosen) {
              const guess = guessFromFilename(chosen.name);
              if (guess.model && !model) setModel(guess.model);
              if (guess.version && !version) setVersion(guess.version);
            }
          }}
        />
        {file ? (
          <p className="ui-hint">
            {file.name} · {formatMB(file.size)}
            {exceedsLimit(file.size)
              ? ` — above the ${UPLOAD_LIMIT_MB} MB limit`
              : ""}
          </p>
        ) : null}
      </div>
      {file && (!model || !version) ? (
        <p className="ui-hint">
          Model and Version are required before uploading (the model must match
          the device rows exactly, e.g. D815).
        </p>
      ) : null}
      <Button
        type="submit"
        disabled={upload.isPending || !file || !model || !version}
      >
        {upload.isPending && file
          ? `Uploading ${formatMB(file.size)}…`
          : "Upload"}
      </Button>
      {sha ? (
        <p>
          Uploaded. SHA-256: <code className="fw-sha">{sha}</code> — verify
          against the vendor's published checksum before activating any rollout.
        </p>
      ) : null}
      {error ? <Alert>{error}</Alert> : null}
    </form>
  );
}

function CreateRolloutForm({
  images,
  devices,
  onDone,
}: {
  images: { id: number; vendor: string; model: string; version: string }[];
  devices: DeviceRow[];
  onDone: () => void;
}): ReactNode {
  const [imageId, setImageId] = useState("");
  const [targetKind, setTargetKind] = useState("test");
  const [selected, setSelected] = useState<number[]>([]);
  const [query, setQuery] = useState("");
  const [hideCurrent, setHideCurrent] = useState(true);
  const [windowStart, setWindowStart] = useState("");
  const [windowEnd, setWindowEnd] = useState("");
  const [error, setError] = useState<string | null>(null);

  // The devices a rollout of the chosen image can target: same vendor and
  // model. `fleet` takes all of them; test/pilot take the ticked subset.
  const image = images.find((i) => String(i.id) === imageId);
  const candidates = image
    ? devices.filter(
        (d) =>
          d.vendor === image.vendor &&
          d.model === image.model &&
          d.adoption_state === "adopted",
      )
    : [];
  const needsSelection = targetKind !== "fleet";
  // Search by extension / MAC / label / model; hide devices already on the
  // image's version by default (a fleet has thousands; the ones left are
  // the ones that matter). Render is capped — refine rather than scroll.
  const shown = filterTargets(candidates, {
    query,
    excludeVersion: hideCurrent && image ? image.version : null,
  });
  const rendered = shown.slice(0, RENDER_CAP);
  const selectShown = (): void =>
    setSelected((cur) =>
      Array.from(new Set([...cur, ...shown.map((d) => d.id)])),
    );
  const toggle = (id: number): void =>
    setSelected((cur) =>
      cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id],
    );

  const create = useMutation({
    mutationFn: () =>
      createFirmwareRollout({
        image_id: Number(imageId),
        target_kind: targetKind,
        target_devices: needsSelection ? selected : undefined,
        window_start: windowStart
          ? new Date(windowStart).toISOString()
          : undefined,
        window_end: windowEnd ? new Date(windowEnd).toISOString() : undefined,
      }),
    onSuccess: () => {
      setError(null);
      setImageId("");
      setSelected([]);
      onDone();
    },
    onError: (err) =>
      setError(
        err instanceof ApiError ? (err.detail ?? err.message) : "failed",
      ),
  });

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    create.mutate();
  };

  return (
    <form className="window-form" onSubmit={submit}>
      <h3>Stage rollout</h3>
      <div className="ui-field">
        <label htmlFor="ro-image">Image</label>
        <select
          id="ro-image"
          value={imageId}
          onChange={(e) => setImageId(e.target.value)}
        >
          <option value="">— select —</option>
          {images.map((i) => (
            <option key={i.id} value={i.id}>
              {i.vendor} {i.model} {i.version}
            </option>
          ))}
        </select>
      </div>
      <div className="ui-field">
        <label htmlFor="ro-target">Target group</label>
        <select
          id="ro-target"
          value={targetKind}
          onChange={(e) => setTargetKind(e.target.value)}
        >
          <option value="test">test — the devices ticked below</option>
          <option value="pilot">pilot — the devices ticked below</option>
          <option value="fleet">
            fleet — every adopted device of this image&apos;s model
          </option>
        </select>
      </div>
      <div className="ui-field">
        <label>
          {needsSelection
            ? `Devices (${selected.length} of ${candidates.length} selected)`
            : `Devices in scope (${candidates.length})`}
        </label>
        {!imageId ? (
          <p className="ui-hint">
            Select an image above — the adopted devices of that model are listed
            here (tick them for test/pilot; fleet takes all of them).
          </p>
        ) : candidates.length === 0 ? (
          <p className="ui-hint">
            No adopted {image?.vendor} {image?.model} devices — the model on the
            image must match the device rows exactly.
          </p>
        ) : (
          <>
            <div className="pairing-actions">
              <input
                id="ro-search"
                type="search"
                placeholder="Search extension, MAC, label or model"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              <label>
                <input
                  type="checkbox"
                  checked={hideCurrent}
                  onChange={(e) => setHideCurrent(e.target.checked)}
                />{" "}
                hide devices already on {image?.version}
              </label>
              {needsSelection ? (
                <>
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={shown.length === 0}
                    onClick={selectShown}
                  >
                    Select all shown ({shown.length})
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={selected.length === 0}
                    onClick={() => setSelected([])}
                  >
                    Clear
                  </Button>
                </>
              ) : null}
            </div>
            <ul className="ui-checklist">
              {rendered.map((d) => (
                <li key={d.id}>
                  <label>
                    <input
                      type="checkbox"
                      checked={!needsSelection || selected.includes(d.id)}
                      disabled={!needsSelection}
                      onChange={() => toggle(d.id)}
                    />{" "}
                    {describeTarget(d)}
                  </label>
                </li>
              ))}
            </ul>
            {shown.length === 0 ? (
              <p className="ui-hint">
                No devices match — widen the search or untick the version
                filter.
              </p>
            ) : null}
            {shown.length > RENDER_CAP ? (
              <p className="ui-hint">
                Showing {RENDER_CAP} of {shown.length} matches — narrow the
                search (Select all shown still selects every match).
              </p>
            ) : null}
          </>
        )}
      </div>
      <div className="ui-field">
        <label htmlFor="ro-start">After-hours window start</label>
        <input
          id="ro-start"
          type="datetime-local"
          value={windowStart}
          onChange={(e) => setWindowStart(e.target.value)}
        />
      </div>
      <div className="ui-field">
        <label htmlFor="ro-end">Window end</label>
        <input
          id="ro-end"
          type="datetime-local"
          value={windowEnd}
          onChange={(e) => setWindowEnd(e.target.value)}
        />
      </div>
      <Button
        type="submit"
        disabled={
          create.isPending ||
          !imageId ||
          (needsSelection && selected.length === 0)
        }
      >
        Stage (firmware = reboot class; after-hours mandatory by default)
      </Button>
      {imageId && needsSelection && selected.length === 0 ? (
        <p className="ui-hint">
          Tick at least one device, or choose the fleet group.
        </p>
      ) : null}
      {error ? <Alert>{error}</Alert> : null}
    </form>
  );
}

function FirmwarePanel(): ReactNode {
  const canManage = usePermission("firmware.manage");
  const qc = useQueryClient();
  const fleet = useQuery({
    queryKey: ["prov", "devices"],
    queryFn: getDevices,
  });
  const images = useQuery({
    queryKey: ["prov", "fw-images"],
    queryFn: getFirmwareImages,
    enabled: canManage,
  });
  const rollouts = useQuery({
    queryKey: ["prov", "fw-rollouts"],
    queryFn: getFirmwareRollouts,
    enabled: canManage,
  });
  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: ["prov", "fw-rollouts"] });
  };
  const invalidateImages = (): void => {
    void qc.invalidateQueries({ queryKey: ["prov", "fw-images"] });
  };
  const activate = useMutation({
    mutationFn: (id: number) => activateRollout(id),
    onSuccess: invalidate,
  });
  const rollback = useMutation({
    mutationFn: (id: number) => rollbackRollout(id),
    onSuccess: invalidate,
  });

  if (!canManage) return null;
  const imageById = new Map((images.data?.items ?? []).map((i) => [i.id, i]));

  return (
    <Panel title="Firmware">
      <h3>Rollouts</h3>
      {(rollouts.data?.items.length ?? 0) === 0 ? (
        <div className="ui-empty">No rollouts staged.</div>
      ) : (
        <table className="ui-table">
          <thead>
            <tr>
              <th>Image</th>
              <th>Targets</th>
              <th>Window</th>
              <th>State</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rollouts.data?.items.map((r) => {
              const img = imageById.get(r.image_id);
              return (
                <tr key={r.id}>
                  <td>
                    {img
                      ? `${img.vendor} ${img.model} ${img.version}`
                      : `#${r.image_id}`}
                  </td>
                  <td>{r.target_kind}</td>
                  <td>
                    {formatWhen(r.window_start)} → {formatWhen(r.window_end)}
                  </td>
                  <td>
                    <Badge tone={ROLLOUT_TONE[r.state] ?? "unknown"}>
                      {r.state}
                    </Badge>
                  </td>
                  <td>
                    <div className="pairing-actions">
                      {["draft", "paused", "staged"].includes(r.state) ? (
                        <Button
                          disabled={activate.isPending}
                          onClick={() => activate.mutate(r.id)}
                        >
                          Activate
                        </Button>
                      ) : null}
                      {r.state === "active" ? (
                        <Button
                          variant="secondary"
                          disabled={rollback.isPending}
                          onClick={() => rollback.mutate(r.id)}
                        >
                          Roll back
                        </Button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {activate.isError ? (
        <Alert>
          Activation refused — likely outside the after-hours window (force is
          deliberate, use the API).
        </Alert>
      ) : null}

      <h3>Images</h3>
      {(images.data?.items.length ?? 0) === 0 ? (
        <div className="ui-empty">No firmware images registered.</div>
      ) : (
        <table className="ui-table">
          <thead>
            <tr>
              <th>Vendor</th>
              <th>Model</th>
              <th>Version</th>
              <th>File</th>
              <th>SHA-256</th>
            </tr>
          </thead>
          <tbody>
            {images.data?.items.map((i) => (
              <tr key={i.id}>
                <td>{i.vendor}</td>
                <td>{i.model}</td>
                <td>{i.version}</td>
                <td>{i.filename}</td>
                <td className="fw-sha">{i.sha256.slice(0, 16)}…</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <UploadImageForm onDone={invalidateImages} />
      <CreateRolloutForm
        images={images.data?.items ?? []}
        devices={fleet.data?.items ?? []}
        onDone={invalidate}
      />
    </Panel>
  );
}

export function DevicesPage(): ReactNode {
  return (
    <div>
      <div className="dash__head">
        <h1>Devices &amp; provisioning</h1>
      </div>
      <AdoptionQueue />
      <DiscoveredDevices />
      <DeviceImport />
      <WindowControls />
      <DeviceInventory />
      <FirmwarePanel />
    </div>
  );
}
